/**
 * useBriefcase
 *
 * Realtime Firestore hook for the Briefcase feature.
 *
 * Collection: briefcase/{userId}/documents/{docId}
 *
 * Behaviour:
 * - Subscribes via onSnapshot for live updates.
 * - On first load, if the collection is empty, seeds it with MOCK_DOCUMENTS
 *   so the UI is never blank.
 * - Falls back gracefully when Firestore is unavailable (permission error
 *   during dev / before deploy) and returns the mock data.
 *
 * CRUD helpers:
 * - addDocument(data)     — optimistic add
 * - updateDocument(id, patch)
 * - deleteDocument(id)
 * - regenerateDocument(id) — clears content and re-sets status to 'draft'
 *                            so the UI can show a regenerating state;
 *                            real content would be produced by a Cloud Function.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  writeBatch,
  getDocs,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../lib/logger';

// ─── Types ────────────────────────────────────────────────────────────────────
export type FileType = 'xlsx' | 'pdf' | 'docx' | 'pptx' | 'eml';
export type DocStatus = 'final' | 'draft' | 'sent' | 'archived';
export type DocCategory =
  | 'reports' | 'emails' | 'pdfs' | 'analyses'
  | 'presentations' | 'drafts' | 'archived';

export interface BriefcaseDoc {
  id: string;
  name: string;
  category: DocCategory;
  status: DocStatus;
  /** ISO date string */
  date: string;
  size: string;
  type: FileType;
  tags: string[];
  origin: string;
  preview: string;
  conversation: string;
  userId: string;
  createdAt?: any;
  _regenerating?: boolean;
}

// ─── Seed data (shown on first load when collection is empty) ─────────────────
const SEED_DOCS: Omit<BriefcaseDoc, 'id'>[] = [
  {
    name: 'Q1 Financial Summary',
    category: 'reports', status: 'final', date: '2026-03-20', size: '2.4 MB', type: 'xlsx',
    tags: ['finance', 'quarterly'],
    origin: 'Voice command',
    preview: 'Revenue grew 23% YoY. EBITDA margin improved to 18.4%. Key drivers include new enterprise contracts and reduced CAC.',
    conversation: 'Fais-moi un résumé financier du Q1',
    userId: '',
  },
  {
    name: 'Investor Pitch Deck v3',
    category: 'presentations', status: 'final', date: '2026-03-19', size: '8.1 MB', type: 'pptx',
    tags: ['investors', 'fundraising', 'pitch'],
    origin: 'Chat',
    preview: '36-slide deck covering market opportunity, product demo, traction metrics, team, and financial projections for Series A.',
    conversation: 'Prépare le pitch deck pour les investisseurs',
    userId: '',
  },
  {
    name: 'Partnership Proposal — Acme Corp',
    category: 'pdfs', status: 'sent', date: '2026-03-18', size: '1.2 MB', type: 'pdf',
    tags: ['partnership', 'acme'],
    origin: 'Voice command',
    preview: 'Proposal outlining integration partnership between HumanReplica.ai and Acme Corp\'s SME platform. Includes revenue share model.',
    conversation: 'Rédige une proposition de partenariat pour Acme Corp et envoie-la à Marc',
    userId: '',
  },
  {
    name: 'Competitor Landscape Analysis',
    category: 'analyses', status: 'final', date: '2026-03-17', size: '3.7 MB', type: 'pdf',
    tags: ['competitive', 'market', 'strategy'],
    origin: 'Chat',
    preview: 'Deep analysis of 12 competitors. Key finding: no competitor offers agent-native document management — the Briefcase is a genuine gap.',
    conversation: 'Analyse les concurrents sur le marché des agents IA pour PME',
    userId: '',
  },
  {
    name: 'Email — Meeting Follow-up (Jean Dupont)',
    category: 'emails', status: 'sent', date: '2026-03-17', size: '12 KB', type: 'eml',
    tags: ['meeting', 'follow-up'],
    origin: 'Voice command',
    preview: 'Follow-up email summarizing key decisions from the March 17 meeting. Action items: finalize pricing, schedule demo.',
    conversation: 'Envoie un email de suivi à Jean après la réunion',
    userId: '',
  },
  {
    name: 'Product Roadmap H2 2026',
    category: 'reports', status: 'draft', date: '2026-03-16', size: '980 KB', type: 'docx',
    tags: ['roadmap', 'product', 'planning'],
    origin: 'Chat',
    preview: '6-month roadmap: Briefcase launch, Voice Module v2, Multi-agent orchestration. Includes resource allocation matrix.',
    conversation: 'Crée un roadmap produit pour le deuxième semestre',
    userId: '',
  },
  {
    name: 'TCO Analysis — Cloud Infrastructure',
    category: 'analyses', status: 'final', date: '2026-03-15', size: '1.8 MB', type: 'xlsx',
    tags: ['infrastructure', 'costs', 'cloud'],
    origin: 'Chat',
    preview: 'Total cost of ownership: AWS vs GCP vs Azure. AWS wins on compute, GCP on AI/ML. Recommendation: hybrid strategy.',
    conversation: 'Compare les coûts d\'infrastructure cloud pour notre stack',
    userId: '',
  },
  {
    name: 'NDA — TechPartners SAS',
    category: 'pdfs', status: 'archived', date: '2026-03-10', size: '340 KB', type: 'pdf',
    tags: ['legal', 'nda'],
    origin: 'Chat',
    preview: 'Mutual NDA between HumanReplica SAS and TechPartners SAS. 2-year term, standard confidentiality clauses.',
    conversation: 'Génère un NDA pour notre partenariat avec TechPartners',
    userId: '',
  },
  {
    name: 'Brand Guidelines Update',
    category: 'drafts', status: 'draft', date: '2026-03-14', size: '5.2 MB', type: 'pdf',
    tags: ['brand', 'design', 'guidelines'],
    origin: 'Chat',
    preview: 'Updated guidelines: void aesthetic, squircle geometry, extended dark UI color palette.',
    conversation: 'Mets à jour le guide de marque avec les dernières modifications',
    userId: '',
  },
  {
    name: 'Weekly Status Report — W12',
    category: 'reports', status: 'sent', date: '2026-03-21', size: '420 KB', type: 'docx',
    tags: ['weekly', 'status', 'team'],
    origin: 'Scheduled',
    preview: 'Week 12: Voice module 80% complete, Briefcase spec finalized, 3 new enterprise leads. Blocker: API rate limiting on Realtime.',
    conversation: 'Génère le rapport hebdomadaire et envoie-le à l\'équipe',
    userId: '',
  },
];

// ─── Hook ─────────────────────────────────────────────────────────────────────
export interface UseBriefcaseReturn {
  documents: BriefcaseDoc[];
  loading: boolean;
  addDocument: (data: Omit<BriefcaseDoc, 'id' | 'userId' | 'createdAt'>) => Promise<void>;
  updateDocument: (id: string, patch: Partial<BriefcaseDoc>) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
  regenerateDocument: (id: string) => Promise<void>;
}

export function useBriefcase(): UseBriefcaseReturn {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<BriefcaseDoc[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Helper: collection ref ──
  const colRef = useCallback(() => {
    if (!user?.uid) throw new Error('User not authenticated');
    return collection(db, 'briefcase', user.uid, 'documents');
  }, [user?.uid]);

  // ── Seed on first load ──
  const seedIfEmpty = useCallback(async (userId: string) => {
    try {
      const col = collection(db, 'briefcase', userId, 'documents');
      const snap = await getDocs(col);
      if (!snap.empty) return;

      logger.debug('[useBriefcase] Collection empty — seeding with demo documents');
      const batch = writeBatch(db);
      SEED_DOCS.forEach((d) => {
        const ref = doc(col);
        batch.set(ref, {
          ...d,
          userId,
          createdAt: serverTimestamp(),
        });
      });
      await batch.commit();
      logger.debug(`[useBriefcase] Seeded ${SEED_DOCS.length} documents`);
    } catch (err) {
      logger.error('[useBriefcase] Seed failed (likely rules not deployed yet):', err);
    }
  }, []);

  // ── Realtime subscription ──
  useEffect(() => {
    if (!user?.uid) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    const uid = user.uid;

    // Seed first, then subscribe
    seedIfEmpty(uid).then(() => {
      const col = collection(db, 'briefcase', uid, 'documents');
      const q = query(col, orderBy('createdAt', 'desc'));

      const unsub = onSnapshot(
        q,
        (snap) => {
          const docs: BriefcaseDoc[] = snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name ?? '',
              category: data.category ?? 'reports',
              status: data.status ?? 'draft',
              date: data.date ?? new Date().toISOString().split('T')[0],
              size: data.size ?? '—',
              type: data.type ?? 'pdf',
              tags: data.tags ?? [],
              origin: data.origin ?? 'Chat',
              preview: data.preview ?? '',
              conversation: data.conversation ?? '',
              userId: data.userId ?? uid,
              createdAt: data.createdAt,
              _regenerating: data._regenerating ?? false,
            } as BriefcaseDoc;
          });
          setDocuments(docs);
          setLoading(false);
          logger.debug(`[useBriefcase] Synced ${docs.length} documents`);
        },
        (err) => {
          logger.error('[useBriefcase] Subscription error (rules not deployed?):', err);
          // Fall back to SEED data rendered locally
          setDocuments(
            SEED_DOCS.map((d, i) => ({ ...d, id: `local-${i}`, userId: uid }))
          );
          setLoading(false);
        }
      );

      return () => unsub();
    });
  }, [user?.uid, seedIfEmpty]);

  // ── CRUD ──
  const addDocument = useCallback(
    async (data: Omit<BriefcaseDoc, 'id' | 'userId' | 'createdAt'>) => {
      if (!user?.uid) return;
      try {
        await addDoc(colRef(), {
          ...data,
          userId: user.uid,
          createdAt: serverTimestamp(),
        });
      } catch (err) {
        logger.error('[useBriefcase] addDocument failed:', err);
      }
    },
    [user?.uid, colRef]
  );

  const updateDocument = useCallback(
    async (id: string, patch: Partial<BriefcaseDoc>) => {
      if (!user?.uid) return;
      try {
        await updateDoc(doc(colRef(), id), { ...patch, _updatedAt: serverTimestamp() });
      } catch (err) {
        logger.error('[useBriefcase] updateDocument failed:', err);
      }
    },
    [user?.uid, colRef]
  );

  const deleteDocument = useCallback(
    async (id: string) => {
      if (!user?.uid) return;
      try {
        await deleteDoc(doc(colRef(), id));
      } catch (err) {
        logger.error('[useBriefcase] deleteDocument failed:', err);
      }
    },
    [user?.uid, colRef]
  );

  /**
   * regenerateDocument — marks the doc as '_regenerating' and reverts it to
   * 'draft' status. A Cloud Function (or a future API call) should pick this
   * up and update the document content asynchronously.
   */
  const regenerateDocument = useCallback(
    async (id: string) => {
      if (!user?.uid) return;
      try {
        await updateDoc(doc(colRef(), id), {
          status: 'draft',
          _regenerating: true,
          _updatedAt: serverTimestamp(),
        });
        // Auto-clear the regenerating flag after 3 s (placeholder)
        setTimeout(async () => {
          try {
            await updateDoc(doc(colRef(), id), {
              _regenerating: false,
              status: 'final',
              _updatedAt: serverTimestamp(),
            });
          } catch { /* ignore */ }
        }, 3000);
      } catch (err) {
        logger.error('[useBriefcase] regenerateDocument failed:', err);
      }
    },
    [user?.uid, colRef]
  );

  return { documents, loading, addDocument, updateDocument, deleteDocument, regenerateDocument };
}
